import React, { useState, useEffect } from 'react';
import axiosInstance from '../services/axiosInstance';
import { PlusIcon, EditIcon, TrashIcon, XIcon, FolderIcon, FileTextIcon, ReceiptIcon, LayoutGridIcon, ListIcon, SparklesIcon } from 'lucide-react';
import { useAuth } from '../App';
import BillTypesTab from './BillTypesTab';

// Pick the right display name for an expense type based on the user's preferred
// UI language. Falls back to whichever value exists.
const getExpenseTypeDisplayName = (item, lang) => {
  if (!item) return '';
  const isEnglish = (lang || 'tr').toLowerCase().startsWith('en');
  if (isEnglish && item.expense_type_name_en) return item.expense_type_name_en;
  if (!isEnglish && item.expense_type_name) return item.expense_type_name;
  // Fallback if preferred language value is missing
  return item.expense_type_name || item.expense_type_name_en || '';
};

const CostCenters = () => {
  const { user } = useAuth();
  const userLang = user?.preferred_language || 'tr';
  const [activeTab, setActiveTab] = useState('projects'); // 'projects' or 'expense-types'
  const [projects, setProjects] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modals
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showExpenseTypeModal, setShowExpenseTypeModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editingExpenseType, setEditingExpenseType] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null);

  // Form states
  const [projectName, setProjectName] = useState('');
  const [expenseTypeName, setExpenseTypeName] = useState('');
  const [activityName, setActivityName] = useState('');

  // View mode for the Expense Types tab: 'grid' (2-col cards) or 'list'
  // (2-col compact rows). Persisted in localStorage so the user's choice
  // survives page reloads.
  const [expenseTypesView, setExpenseTypesView] = useState(() => {
    if (typeof window === 'undefined') return 'grid';
    return localStorage.getItem('cc_expense_types_view') || 'grid';
  });
  useEffect(() => {
    try {
      localStorage.setItem('cc_expense_types_view', expenseTypesView);
    } catch (_e) {
      /* ignore quota / private-mode errors */
    }
  }, [expenseTypesView]);

  // Same 2-col Kart / Liste toggle for the Projeler tab. Independent
  // preference so the user can e.g. keep Projects in Liste and Expense
  // Types in Kart if they want.
  const [projectsView, setProjectsView] = useState(() => {
    if (typeof window === 'undefined') return 'grid';
    return localStorage.getItem('cc_projects_view') || 'grid';
  });
  useEffect(() => {
    try {
      localStorage.setItem('cc_projects_view', projectsView);
    } catch (_e) { /* ignore */ }
  }, [projectsView]);

  // Activities view — bağımsız (kullanıcı Projeler'de Kart, Aktivitelerde Liste seçebilir).
  const [activitiesView, setActivitiesView] = useState(() => {
    if (typeof window === 'undefined') return 'grid';
    return localStorage.getItem('cc_activities_view') || 'grid';
  });
  useEffect(() => {
    try {
      localStorage.setItem('cc_activities_view', activitiesView);
    } catch (_e) { /* ignore */ }
  }, [activitiesView]);

  // Fetch projects
  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/cost-centers/projects');
      setProjects(response.data);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Projeler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Fetch expense types
  const fetchExpenseTypes = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/cost-centers/expense-types');
      setExpenseTypes(response.data);
    } catch (err) {
      console.error('Error fetching expense types:', err);
      setError('Genel gider türleri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Fetch activities — kullanıcı 'activities' sekmesine geçince tetiklenir.
  const fetchActivities = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/cost-centers/activities');
      setActivities(response.data);
    } catch (err) {
      console.error('Error fetching activities:', err);
      setError('Aktiviteler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'projects') {
      fetchProjects();
    } else if (activeTab === 'activities') {
      fetchActivities();
    } else if (activeTab === 'expense-types') {
      fetchExpenseTypes();
    }
  }, [activeTab]);

  // Project handlers
  const handleSaveProject = async () => {
    if (!projectName.trim()) {
      setError('Proje adı boş olamaz');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      if (editingProject) {
        // Update
        await axiosInstance.put(`/cost-centers/projects/${editingProject.id}`, {
          project_name: projectName
        });
        setSuccess('Proje başarıyla güncellendi');
      } else {
        // Create
        await axiosInstance.post('/cost-centers/projects', {
          project_name: projectName
        });
        setSuccess('Proje başarıyla eklendi');
      }

      setShowProjectModal(false);
      setProjectName('');
      setEditingProject(null);
      fetchProjects();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving project:', err);
      setError(err.response?.data?.detail || 'Proje kaydedilirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Bu projeyi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      await axiosInstance.delete(`/cost-centers/projects/${projectId}`);
      setSuccess('Proje başarıyla silindi');
      fetchProjects();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting project:', err);
      setError(err.response?.data?.detail || 'Proje silinirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Expense type handlers
  const handleSaveExpenseType = async () => {
    if (!expenseTypeName.trim()) {
      setError('Genel gider türü adı boş olamaz');
      return;
    }

    // Decide which field the user is editing based on their preferred language.
    // We preserve both names; only the language-specific field gets the new value.
    const isEnglish = (userLang || 'tr').toLowerCase().startsWith('en');
    const trimmed = expenseTypeName.trim();

    const buildPayload = (existing) => {
      // If we are editing, keep the "other" language untouched; only update the
      // language the user actually sees.
      if (existing) {
        if (isEnglish) {
          return {
            expense_type_name: existing.expense_type_name || trimmed,
            expense_type_name_en: trimmed,
          };
        }
        return {
          expense_type_name: trimmed,
          expense_type_name_en: existing.expense_type_name_en || null,
        };
      }
      // For new entries, store the value in the language the user is using.
      if (isEnglish) {
        return {
          expense_type_name: trimmed,
          expense_type_name_en: trimmed,
        };
      }
      return {
        expense_type_name: trimmed,
        expense_type_name_en: null,
      };
    };

    try {
      setLoading(true);
      setError('');
      
      if (editingExpenseType) {
        // Update
        await axiosInstance.put(
          `/cost-centers/expense-types/${editingExpenseType.id}`,
          buildPayload(editingExpenseType)
        );
        setSuccess('Genel gider türü başarıyla güncellendi');
      } else {
        // Create
        await axiosInstance.post('/cost-centers/expense-types', buildPayload(null));
        setSuccess('Genel gider türü başarıyla eklendi');
      }

      setShowExpenseTypeModal(false);
      setExpenseTypeName('');
      setEditingExpenseType(null);
      fetchExpenseTypes();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving expense type:', err);
      setError(err.response?.data?.detail || 'Genel gider türü kaydedilirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExpenseType = async (typeId) => {
    if (!window.confirm('Bu genel gider türünü silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      await axiosInstance.delete(`/cost-centers/expense-types/${typeId}`);
      setSuccess('Genel gider türü başarıyla silindi');
      fetchExpenseTypes();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting expense type:', err);
      setError(err.response?.data?.detail || 'Genel gider türü silinirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Activity handlers — Projeler ile birebir aynı desende.
  const handleSaveActivity = async () => {
    if (!activityName.trim()) {
      setError('Aktivite adı boş olamaz');
      return;
    }
    try {
      setLoading(true);
      setError('');
      if (editingActivity) {
        await axiosInstance.put(`/cost-centers/activities/${editingActivity.id}`, {
          activity_name: activityName,
        });
        setSuccess('Aktivite başarıyla güncellendi');
      } else {
        await axiosInstance.post('/cost-centers/activities', {
          activity_name: activityName,
        });
        setSuccess('Aktivite başarıyla eklendi');
      }
      setShowActivityModal(false);
      setActivityName('');
      setEditingActivity(null);
      fetchActivities();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving activity:', err);
      setError(err.response?.data?.detail || 'Aktivite kaydedilirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm('Bu aktiviteyi silmek istediğinizden emin misiniz?')) {
      return;
    }
    try {
      setLoading(true);
      setError('');
      await axiosInstance.delete(`/cost-centers/activities/${activityId}`);
      setSuccess('Aktivite başarıyla silindi');
      fetchActivities();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting activity:', err);
      setError(err.response?.data?.detail || 'Aktivite silinirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Masraf Merkezleri</h2>
        <p className="text-sm text-gray-600 mt-1">
          Harcamaları projelere veya genel gider türlerine atamak için masraf merkezlerini yönetin
        </p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg">
          {success}
        </div>
      )}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex space-x-8">
          <button
            onClick={() => setActiveTab('projects')}
            className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'projects'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <FolderIcon className="w-4 h-4" />
              <span>Projeler</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('activities')}
            className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'activities'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
            }`}
            data-testid="cost-center-tab-activities"
          >
            <div className="flex items-center space-x-2">
              <SparklesIcon className="w-4 h-4" />
              <span>Aktiviteler</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('expense-types')}
            className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'expense-types'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <FileTextIcon className="w-4 h-4" />
              <span>Genel Gider Türleri</span>
            </div>
          </button>
          {/* Faturalar tab — Solo plan only (utility bill categories) */}
          {(user?.company?.subscription_plan === 'solo') && (
          <button
            onClick={() => setActiveTab('bills')}
            className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'bills'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
            }`}
            data-testid="cost-center-tab-bills"
          >
            <div className="flex items-center space-x-2">
              <ReceiptIcon className="w-4 h-4" />
              <span>Faturalar</span>
            </div>
          </button>
          )}
        </div>
      </div>

      {/* Content */}
      {activeTab === 'bills' ? (
        <BillTypesTab />
      ) : activeTab === 'projects' ? (
        <div>
          {/* Add Project Button + View Mode Toggle */}
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5" data-testid="projects-view-toggle">
              <button
                type="button"
                onClick={() => setProjectsView('grid')}
                className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors ${
                  projectsView === 'grid'
                    ? 'bg-teal-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                data-testid="projects-view-grid"
              >
                <LayoutGridIcon className="w-4 h-4" /> Kart
              </button>
              <button
                type="button"
                onClick={() => setProjectsView('list')}
                className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors ${
                  projectsView === 'list'
                    ? 'bg-teal-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                data-testid="projects-view-list"
              >
                <ListIcon className="w-4 h-4" /> Liste
              </button>
            </div>

            <button
              onClick={() => {
                setEditingProject(null);
                setProjectName('');
                setShowProjectModal(true);
              }}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center space-x-2"
            >
              <PlusIcon className="w-4 h-4" />
              <span>Yeni Proje Ekle</span>
            </button>
          </div>

          {/* Projects — Grid / List */}
          {loading ? (
            <div className="text-center py-8 text-gray-600">Yükleniyor...</div>
          ) : projects.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <FolderIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">Henüz proje eklenmemiş</p>
              <p className="text-sm text-gray-500 mt-1">Yeni proje eklemek için yukarıdaki butona tıklayın</p>
            </div>
          ) : projectsView === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="projects-grid">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className="group bg-white rounded-lg border border-gray-200 hover:border-teal-400 hover:shadow-sm transition-all p-4 flex items-center gap-3"
                  data-testid={`project-card-${project.id}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                    <FolderIcon className="w-5 h-5 text-teal-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{project.project_name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {new Date(project.created_at).toLocaleDateString('tr-TR')}
                    </div>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                    <button
                      onClick={() => {
                        setEditingProject(project);
                        setProjectName(project.project_name);
                        setShowProjectModal(true);
                      }}
                      className="p-1.5 rounded hover:bg-teal-50 text-teal-600"
                      title="Düzenle"
                    >
                      <EditIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteProject(project.id)}
                      className="p-1.5 rounded hover:bg-red-50 text-red-600"
                      title="Sil"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 bg-white rounded-lg border border-gray-200 p-2" data-testid="projects-list">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-gray-50 group"
                  data-testid={`project-row-${project.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FolderIcon className="w-4 h-4 text-teal-600 flex-shrink-0" />
                    <span className="text-sm text-gray-900 truncate">{project.project_name}</span>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => {
                        setEditingProject(project);
                        setProjectName(project.project_name);
                        setShowProjectModal(true);
                      }}
                      className="p-1 rounded hover:bg-teal-50 text-teal-600"
                      title="Düzenle"
                    >
                      <EditIcon className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDeleteProject(project.id)}
                      className="p-1 rounded hover:bg-red-50 text-red-600"
                      title="Sil"
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : activeTab === 'activities' ? (
        <div>
          {/* Add Activity Button + View Mode Toggle — Projeler ile birebir aynı yapı */}
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5" data-testid="activities-view-toggle">
              <button
                type="button"
                onClick={() => setActivitiesView('grid')}
                className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors ${
                  activitiesView === 'grid'
                    ? 'bg-teal-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                data-testid="activities-view-grid"
              >
                <LayoutGridIcon className="w-4 h-4" /> Kart
              </button>
              <button
                type="button"
                onClick={() => setActivitiesView('list')}
                className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors ${
                  activitiesView === 'list'
                    ? 'bg-teal-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                data-testid="activities-view-list"
              >
                <ListIcon className="w-4 h-4" /> Liste
              </button>
            </div>

            <button
              onClick={() => {
                setEditingActivity(null);
                setActivityName('');
                setShowActivityModal(true);
              }}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center space-x-2"
              data-testid="activities-add-btn"
            >
              <PlusIcon className="w-4 h-4" />
              <span>Yeni Aktivite Ekle</span>
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-600">Yükleniyor...</div>
          ) : activities.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300" data-testid="activities-empty">
              <SparklesIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">Henüz aktivite eklenmemiş</p>
              <p className="text-sm text-gray-500 mt-1">
                Yaz tatili, doğum günü gibi aktiviteler ekleyin ve şahsi harcamalarınızı bunlara bağlayın
              </p>
            </div>
          ) : activitiesView === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="activities-grid">
              {activities.map((activity) => (
                <div
                  key={activity.id}
                  className="group bg-white rounded-lg border border-gray-200 hover:border-teal-400 hover:shadow-sm transition-all p-4 flex items-center gap-3"
                  data-testid={`activity-card-${activity.id}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                    <SparklesIcon className="w-5 h-5 text-teal-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{activity.activity_name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {new Date(activity.created_at).toLocaleDateString('tr-TR')}
                    </div>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                    <button
                      onClick={() => {
                        setEditingActivity(activity);
                        setActivityName(activity.activity_name);
                        setShowActivityModal(true);
                      }}
                      className="p-1.5 rounded hover:bg-teal-50 text-teal-600"
                      title="Düzenle"
                      data-testid={`activity-edit-${activity.id}`}
                    >
                      <EditIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteActivity(activity.id)}
                      className="p-1.5 rounded hover:bg-red-50 text-red-600"
                      title="Sil"
                      data-testid={`activity-delete-${activity.id}`}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 bg-white rounded-lg border border-gray-200 p-2" data-testid="activities-list">
              {activities.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-gray-50 group"
                  data-testid={`activity-row-${activity.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <SparklesIcon className="w-4 h-4 text-teal-600 flex-shrink-0" />
                    <span className="text-sm text-gray-900 truncate">{activity.activity_name}</span>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => {
                        setEditingActivity(activity);
                        setActivityName(activity.activity_name);
                        setShowActivityModal(true);
                      }}
                      className="p-1 rounded hover:bg-teal-50 text-teal-600"
                      title="Düzenle"
                    >
                      <EditIcon className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDeleteActivity(activity.id)}
                      className="p-1 rounded hover:bg-red-50 text-red-600"
                      title="Sil"
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div>
          {/* Add Expense Type Button + View Mode Toggle */}
          <div className="mb-4 flex items-center justify-between gap-3">
            {/* View toggle — Grid | List. Both modes lay entries out in
                2 columns (kullanıcı isteği); the difference is card vs. row. */}
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5" data-testid="expense-types-view-toggle">
              <button
                type="button"
                onClick={() => setExpenseTypesView('grid')}
                className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors ${
                  expenseTypesView === 'grid'
                    ? 'bg-teal-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                data-testid="expense-types-view-grid"
              >
                <LayoutGridIcon className="w-4 h-4" /> Kart
              </button>
              <button
                type="button"
                onClick={() => setExpenseTypesView('list')}
                className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors ${
                  expenseTypesView === 'list'
                    ? 'bg-teal-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                data-testid="expense-types-view-list"
              >
                <ListIcon className="w-4 h-4" /> Liste
              </button>
            </div>

            <button
              onClick={() => {
                setEditingExpenseType(null);
                setExpenseTypeName('');
                setShowExpenseTypeModal(true);
              }}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center space-x-2"
            >
              <PlusIcon className="w-4 h-4" />
              <span>Yeni Genel Gider Türü Ekle</span>
            </button>
          </div>

          {/* Expense Types — Grid / List */}
          {loading ? (
            <div className="text-center py-8 text-gray-600">Yükleniyor...</div>
          ) : expenseTypes.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <FileTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">Henüz genel gider türü eklenmemiş</p>
              <p className="text-sm text-gray-500 mt-1">Yeni genel gider türü eklemek için yukarıdaki butona tıklayın</p>
            </div>
          ) : expenseTypesView === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="expense-types-grid">
              {expenseTypes.map((expenseType) => (
                <div
                  key={expenseType.id}
                  className="group bg-white rounded-lg border border-gray-200 hover:border-teal-400 hover:shadow-sm transition-all p-4 flex items-center gap-3"
                  data-testid={`expense-type-card-${expenseType.id}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                    <FileTextIcon className="w-5 h-5 text-teal-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {getExpenseTypeDisplayName(expenseType, userLang)}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {new Date(expenseType.created_at).toLocaleDateString('tr-TR')}
                    </div>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                    <button
                      onClick={() => {
                        setEditingExpenseType(expenseType);
                        setExpenseTypeName(getExpenseTypeDisplayName(expenseType, userLang));
                        setShowExpenseTypeModal(true);
                      }}
                      className="p-1.5 rounded hover:bg-teal-50 text-teal-600"
                      title="Düzenle"
                    >
                      <EditIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteExpenseType(expenseType.id)}
                      className="p-1.5 rounded hover:bg-red-50 text-red-600"
                      title="Sil"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* List view — 2-column compact rows. */
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 bg-white rounded-lg border border-gray-200 p-2" data-testid="expense-types-list">
              {expenseTypes.map((expenseType) => (
                <div
                  key={expenseType.id}
                  className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-gray-50 group"
                  data-testid={`expense-type-row-${expenseType.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileTextIcon className="w-4 h-4 text-teal-600 flex-shrink-0" />
                    <span className="text-sm text-gray-900 truncate">
                      {getExpenseTypeDisplayName(expenseType, userLang)}
                    </span>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => {
                        setEditingExpenseType(expenseType);
                        setExpenseTypeName(getExpenseTypeDisplayName(expenseType, userLang));
                        setShowExpenseTypeModal(true);
                      }}
                      className="p-1 rounded hover:bg-teal-50 text-teal-600"
                      title="Düzenle"
                    >
                      <EditIcon className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDeleteExpenseType(expenseType.id)}
                      className="p-1 rounded hover:bg-red-50 text-red-600"
                      title="Sil"
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Project Modal */}
      {showProjectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {editingProject ? 'Proje Düzenle' : 'Yeni Proje Ekle'}
              </h3>
              <button
                onClick={() => {
                  setShowProjectModal(false);
                  setProjectName('');
                  setEditingProject(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Proje Adı
              </label>
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Örn: Mobil Uygulama Projesi"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowProjectModal(false);
                  setProjectName('');
                  setEditingProject(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                İptal
              </button>
              <button
                onClick={handleSaveProject}
                disabled={loading}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
              >
                {loading ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Expense Type Modal */}
      {showExpenseTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {editingExpenseType ? 'Genel Gider Türü Düzenle' : 'Yeni Genel Gider Türü Ekle'}
              </h3>
              <button
                onClick={() => {
                  setShowExpenseTypeModal(false);
                  setExpenseTypeName('');
                  setEditingExpenseType(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Genel Gider Türü Adı
              </label>
              <input
                type="text"
                value={expenseTypeName}
                onChange={(e) => setExpenseTypeName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Örn: Ofis Malzemeleri"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowExpenseTypeModal(false);
                  setExpenseTypeName('');
                  setEditingExpenseType(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                İptal
              </button>
              <button
                onClick={handleSaveExpenseType}
                disabled={loading}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
              >
                {loading ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activity Modal */}
      {showActivityModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {editingActivity ? 'Aktivite Düzenle' : 'Yeni Aktivite Ekle'}
              </h3>
              <button
                onClick={() => {
                  setShowActivityModal(false);
                  setActivityName('');
                  setEditingActivity(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Aktivite Adı
              </label>
              <input
                type="text"
                value={activityName}
                onChange={(e) => setActivityName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Örn: Yaz Tatili"
                data-testid="activity-name-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Örn: Yaz Tatili, Doğum Günü, Ev Taşıma — Şahsi harcamalarınızı bu aktivitelere bağlayabilirsiniz.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowActivityModal(false);
                  setActivityName('');
                  setEditingActivity(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                İptal
              </button>
              <button
                onClick={handleSaveActivity}
                disabled={loading}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                data-testid="activity-save-btn"
              >
                {loading ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CostCenters;
